import { useCallback, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useUniverses } from '@biasafe.queries/securities-universes-data/securities-universes-data';
import { UNIVERSE_SCOPES } from '../../config/constants/pointInTimeData';
import {
  FALLBACK_UNIVERSE,
  STATIC_COUNTRY_COUNTS,
  STATIC_UNIVERSES,
} from '../../config/constants/staticUniverses';
import { useClassificationData } from '../eligibility/useClassificationData';
import type { SuperSector, Universe } from '@biasafe.models';
import { useStrategyPresets } from '@/features/Strategies/features/SmartStrategyDefinitonForm/Steps/hooks/useStrategyPresets';
import {
  selectDataProcessor,
  useSmartBetaStrategyStore,
} from '@/features/Strategies/stores/definitionFormStore';
import { useUniverseStats } from '@/features/Strategies/features/SmartStrategyDefinitonForm/hooks/useUniverseStats';
import { toDataStatus } from '@/features/Strategies/features/SmartStrategyDefinitonForm/hooks/dataStatus';
import { useFilteredSecurities } from '@/features/Strategies/features/SmartStrategyDefinitonForm/hooks/useFilteredSecurities';
import { universeCatalogueQuery } from '@/shared/config/queryDefaults';
import { useUniverseAsOfDate } from '../../../RebalancingSchedule/hooks/useLatestReconstitutionDate';

const SIZE_LABEL_TO_UI: Record<string, string> = {
  LRG: 'large',
  MID: 'mid',
  SML: 'small',
};

// Wizard-filter defaults used to reset state when a fresh universe is
// selected. A previous strategy's REIT exclusion / size segment toggle /
// liquidity slider would otherwise carry over and silently narrow the
// new universe (e.g. US Target Market Exposure showing 431 instead of
// 490 when a prior session unchecked Small Cap or REITs). Mirrors the
// initial state in stores/slices/dataProcessorSlice.ts.
const WIZARD_FILTER_DEFAULTS = {
  size_segment: ['large', 'mid', 'small'],
  cyclicality: ['cyclical', 'defensive', 'sensitive'] as Array<string>,
  exclude_type: {
    common: false,
    preferred: false,
    reit: false,
    etf: false,
    Adr: false,
    right: false,
    warrants: false,
    units: false,
    tracking_stock: false,
    closed_end_fund: false,
  },
  exclude_exchanges: [] as Array<string>,
  liquidity: {
    price_range: { min: 0, max: Number.MAX_SAFE_INTEGER },
    avg_dollar_volume: { min: 0, max: Number.MAX_SAFE_INTEGER },
    // Uncapped like the others — a concrete 100 rendered as an explicit
    // "100.00%" cap (a no-op, since 100% is the physical ceiling) that read as
    // an ACTIVE filter the moment the user switched universes and this reset
    // ran. The MAX_SAFE_INTEGER sentinel is the true "no cap" default and shows
    // "+ Max"; HistogramRangeFilter's valueMax={100} still clamps typed input.
    float: { min: 0, max: Number.MAX_SAFE_INTEGER },
  },
  size: { min: 0, max: Number.MAX_SAFE_INTEGER },
  history: { min: 0, max: Number.MAX_SAFE_INTEGER },
  single_stock_restriction: {
    blocked: [] as Array<string>,
  },
};

export function usePointInTime() {
  // The catalogue is STATIC data the API serves regardless of ClickHouse health,
  // so its length says nothing about whether CH is reachable — it only gates
  // the catalogue-owned facts (coverage). The MEASURED numbers get their own
  // signal from the securities query; see `metricsStatus` below.
  // Step 2's latest reconstitution date. The universe is resolved AT that
  // date so the eligible set and the portfolio it will rebalance are counted
  // at the same instant; it is also part of the queryKey, so changing the
  // schedule refetches instead of serving the previous date from cache.
  const asOf = useUniverseAsOfDate();
  const { data: chUniverses = [], isLoading: isCatalogueLoading } =
    useUniverses(asOf ? { as_of: asOf } : undefined, {
      query: universeCatalogueQuery(asOf),
    });
  const catalogueStatus = toDataStatus(
    isCatalogueLoading,
    chUniverses.length > 0,
  );
  const universes = chUniverses.length ? chUniverses : STATIC_UNIVERSES;

  const { geographicScope, sizeSegment, cyclicality, portfolioId } =
    useSmartBetaStrategyStore(
      useShallow((state) => {
        const u = selectDataProcessor(state).universe;
        // Coerce each multi-select field to a concrete array so downstream
        // consumers (MultiSelectCardGroup, the toggle handlers, the
        // coverage compute) never see undefined. A Morningstar universe
        // whose ID isn't in UNIVERSE_SCOPES would leave these as
        // undefined in the store, which used to crash the page with
        // "Cannot read properties of undefined (reading 'includes')".
        return {
          geographicScope: u.geographic_scope ?? [],
          sizeSegment: u.size_segment ?? [],
          cyclicality: u.cyclicality ?? [],
          portfolioId: u.portfolio_id,
        };
      }),
    );

  const setUniverse = useSmartBetaStrategyStore((s) => s.setUniverse);
  const setExcludeCountries = useSmartBetaStrategyStore(
    (s) => s.setExcludeCountries,
  );
  const setExcludeGicsSector = useSmartBetaStrategyStore(
    (s) => s.setExcludeGicsSector,
  );
  const setExcludeGicsIndustries = useSmartBetaStrategyStore(
    (s) => s.setExcludeGicsIndustries,
  );
  const setExcludeGicsSubIndustries = useSmartBetaStrategyStore(
    (s) => s.setExcludeGicsSubIndustries,
  );
  const setExcludeType = useSmartBetaStrategyStore((s) => s.setExcludeType);
  const setExcludeExchanges = useSmartBetaStrategyStore(
    (s) => s.setExcludeExchanges,
  );
  const setLiquidity = useSmartBetaStrategyStore((s) => s.setLiquidity);
  const setSize = useSmartBetaStrategyStore((s) => s.setSize);
  const setHistory = useSmartBetaStrategyStore((s) => s.setHistory);
  const setSingleStockRestriction = useSmartBetaStrategyStore(
    (s) => s.setSingleStockRestriction,
  );
  const { setRebalancerForUniverse } = useStrategyPresets();

  const selectedUniverse = useMemo<Universe>(
    () =>
      universes.find((u: Universe) => u.id === portfolioId) ??
      // The static catalogue carries the SAME ids, so a CH-down catalogue still
      // resolves the user's actual pick instead of silently substituting the
      // first entry — which then scoped the stats to every security in the
      // selected regions rather than this index's constituents.
      STATIC_UNIVERSES.find((u) => u.id === portfolioId) ??
      universes[0] ??
      FALLBACK_UNIVERSE,
    [universes, portfolioId],
  );

  const universeId = selectedUniverse?.id ?? '';
  // Available geographic scopes come from the universe's own
  // `geographicScopes` (real API field, same vocabulary as the legacy
  // UNIVERSE_SCOPES map). Fall back to the static map only if the API
  // hasn't populated the field, so a new/custom universe still works.
  const availableScopes = selectedUniverse?.geographicScopes?.length
    ? selectedUniverse.geographicScopes
    : (UNIVERSE_SCOPES[universeId] ?? []);

  const handleUniverseSelect = useCallback(
    (id: string) => {
      const nextUniverse = universes.find((u: Universe) => u.id === id);
      if (!nextUniverse) return;

      // Geographic scopes come from the universe's own `geographicScopes`
      // (real API field); fall back to the static UNIVERSE_SCOPES map, then
      // to all visible scopes, so the size / cyclicality cascades always
      // have something to filter against. Never undefined — that crashed
      // the MultiSelectCardGroup the moment the new universe rendered.
      setUniverse({
        portfolio_id: nextUniverse.id,
        geographic_scope: nextUniverse.geographicScopes?.length
          ? nextUniverse.geographicScopes
          : (UNIVERSE_SCOPES[id] ?? ['north_america', 'dev_ex_na', 'emerging']),
        size_segment: WIZARD_FILTER_DEFAULTS.size_segment,
        cyclicality: WIZARD_FILTER_DEFAULTS.cyclicality,
      });

      // Full filter reset — the new universe defines a fresh base
      // population, so every wizard-side narrowing toggle from a prior
      // strategy must clear. Without this, opening a new strategy with a
      // previous session's REIT exclusion / unchecked Small Cap /
      // liquidity slider silently narrows the count (490 → 431 etc.)
      // and the user blames the data.
      setExcludeCountries([]);
      setExcludeGicsSector([]);
      setExcludeGicsIndustries([]);
      setExcludeGicsSubIndustries([]);
      setExcludeType(WIZARD_FILTER_DEFAULTS.exclude_type);
      setExcludeExchanges(WIZARD_FILTER_DEFAULTS.exclude_exchanges);
      setLiquidity(WIZARD_FILTER_DEFAULTS.liquidity);
      setSize(WIZARD_FILTER_DEFAULTS.size);
      setHistory(WIZARD_FILTER_DEFAULTS.history);
      setSingleStockRestriction(
        WIZARD_FILTER_DEFAULTS.single_stock_restriction,
      );

      setRebalancerForUniverse(nextUniverse.rebalancerDetails);
    },
    [
      universes,
      setUniverse,
      setExcludeCountries,
      setExcludeGicsSector,
      setExcludeGicsIndustries,
      setExcludeGicsSubIndustries,
      setExcludeType,
      setExcludeExchanges,
      setLiquidity,
      setSize,
      setHistory,
      setSingleStockRestriction,
      setRebalancerForUniverse,
    ],
  );

  const handleGeographicToggle = useCallback(
    (scopeId: string) => {
      const next = geographicScope.includes(scopeId)
        ? geographicScope.filter((s) => s !== scopeId)
        : [...geographicScope, scopeId];

      if (next.length > 0) {
        setUniverse({ geographic_scope: next });
      }
    },
    [geographicScope, setUniverse],
  );

  const handleSizeToggle = useCallback(
    (segmentId: string) => {
      const next = sizeSegment.includes(segmentId)
        ? sizeSegment.filter((s) => s !== segmentId)
        : [...sizeSegment, segmentId];

      if (next.length > 0) {
        setUniverse({ size_segment: next });
      }
    },
    [sizeSegment, setUniverse],
  );

  const handleCyclicalityToggle = useCallback(
    (cycleId: string) => {
      const next = cyclicality.includes(cycleId)
        ? cyclicality.filter((c) => c !== cycleId)
        : [...cyclicality, cycleId];

      if (next.length > 0) {
        setUniverse({ cyclicality: next });
      }
    },
    [cyclicality, setUniverse],
  );

  const {
    countries: universeCountries,
    countriesInitial,
    constituents,
    universeSize,
    marketCap,
    universeMarketCap,
    isLoading: isStatsLoading,
    status: metricsStatus,
  } = useUniverseStats();

  const {
    // Layer 1 — the sectors the SELECTED UNIVERSE contains, before any
    // wizard-side filter. Deliberately NOT `availableSectorCodes` (Layer 3);
    // see `disabledCyclicality` below for why that one was unusable.
    universeSectorCodes,
    filterExcludedCounts,
  } = useFilteredSecurities();

  // Rows the Primary-Share-Class rule drops, so the headline constituent count
  // matches what the engine will actually feed the backtest. Computed once in
  // useFilteredSecurities' single pass over Layer 2 (universe + scope + size +
  // cyclicality) — the engine applies structural rules before the eligibility
  // filters, so a sector or country exclusion must not move it.
  const primaryShareClassExcluded =
    filterExcludedCounts.primaryShareClassExcluded;
  const { sectors: allSectors } = useClassificationData();

  // Geographic scopes not covered by this universe are disabled — but an EMPTY
  // scope list disables nothing. Same rule as size and cyclicality below: no
  // scope information is not evidence of absence. This currently fails open
  // only by coincidence, because UNIVERSE_SCOPES' six keys happen to match
  // UNIVERSE_STATIC_DATA's six; a seventh index added backend-side without
  // touching the frontend map would grey out EVERY scope button and hard-block
  // step 1, since the group requires at least one selection.
  const disabledScopes = useMemo(() => {
    if (availableScopes.length === 0) return new Set<string>();
    const disabled = new Set<string>();
    for (const scope of ['north_america', 'dev_ex_na', 'emerging'] as const) {
      if (!availableScopes.includes(scope)) {
        disabled.add(scope);
      }
    }
    return disabled;
  }, [availableScopes]);

  // Size segments not available in the universe (Layer 1) are disabled — but
  // ONLY once real CH size metrics have arrived. Gate on the metric itself, not
  // on whether the catalogue loaded: the size mix can be empty while the
  // (static) catalogue is fully present — CH down, cold, or still loading — and
  // the segment buttons must stay freely selectable then (nothing here depends
  // on ClickHouse).
  const disabledSizeSegments = useMemo(() => {
    const universeLabels = new Set<string>(
      (selectedUniverse.sizeSegments ?? []).map((s) => s.label),
    );
    if (universeLabels.size === 0) return new Set<string>();
    const disabled = new Set<string>();
    for (const [apiLabel, uiId] of Object.entries(SIZE_LABEL_TO_UI)) {
      if (!universeLabels.has(apiLabel)) {
        disabled.add(uiId);
      }
    }
    return disabled;
  }, [selectedUniverse]);

  // Cyclicality buckets the selected universe simply doesn't contain — but ONLY
  // once the (CH-derived) sector data has loaded. While it's absent, never
  // disable: the buttons must not depend on ClickHouse.
  //
  // Computed from Layer 1 (`universeSectorCodes`), NOT Layer 3
  // (`availableSectorCodes`). Layer 3 is the post-filter population, and
  // cyclicality is itself applied in Layer 2 — so reading it here made the
  // control disable itself: deselecting "Cyclical" removed every cyclical
  // security, which removed their sectors from the available set, which left
  // the cyclical bucket with no active super-sector, which greyed the button
  // out. The user could switch it off and then never switch it back on.
  //
  // Size Segment never had this bug because it reads `selectedUniverse
  // .sizeSegments` — the index's own size mix, which no wizard filter can move.
  // Layer 1 is the equivalent invariant for sectors.
  const disabledCyclicality = useMemo(() => {
    if (universeSectorCodes.size === 0) return new Set<string>();
    const universeSuperSectors = new Set(
      allSectors
        .filter((s) => universeSectorCodes.has(s.code))
        .map((s) => s.superSector),
    );
    const disabled = new Set<string>();
    for (const cycle of ['cyclical', 'defensive', 'sensitive'] as const) {
      if (!universeSuperSectors.has(cycle as SuperSector)) disabled.add(cycle);
    }
    return disabled;
  }, [universeSectorCodes, allSectors]);

  const stats = useMemo(() => {
    // The index's published coverage of the investable universe (e.g. 85% for
    // US Target Market Exposure), served by the API alongside the other index
    // facts in this row. It used to be the sum of the SELECTED size segments'
    // coverage, which is ~100% whenever every segment is selected — i.e. the
    // default — so the stat read "100%" for every index and told the user
    // nothing.
    // Scale the published coverage DOWN by the share of the universe's market
    // cap left after the user's scope/size/exclusions (a rule of three): the
    // full universe reads the published figure; excluding names drops it
    // proportionally to the market cap removed.
    const coverageFallback = selectedUniverse?.coverageScope ?? 0;
    const coverage =
      universeMarketCap > 0
        ? coverageFallback * (marketCap / universeMarketCap)
        : coverageFallback;

    // Country coverage is a published property of the index, so it renders from
    // the static catalogue until the live count arrives — only THEN does it
    // reflect the user's own country exclusions. Constituents and market cap
    // have no such published value and stay unavailable until measured.
    const countries =
      metricsStatus === 'ready'
        ? universeCountries
        : (STATIC_COUNTRY_COUNTS[universeId] ?? 0);

    return {
      // `countries` above already falls back to the index's published count
      // while the live metrics are still in flight, which `universeCountries`
      // alone does not; #1207's primary-share-class rule applies to the
      // constituent count on top of that.
      countries,
      constituents: Math.max(0, constituents - primaryShareClassExcluded),
      // Raw universe size (Layer 1 — before scope/size/type/GICS/liquidity
      // filters). UniversePreview uses this to render "constituents /
      // universeSize" so a filter-narrowed count doesn't read as missing data.
      universeSize,
      coverage,
      // Initial (full-universe) values — the "current / initial" denominators
      // the stats row shows once the matching stat is actually reduced.
      coverageInitial: coverageFallback,
      marketCap,
      marketCapInitial: universeMarketCap,
      countriesInitial,
    };
  }, [
    universeCountries,
    countriesInitial,
    constituents,
    primaryShareClassExcluded,
    universeSize,
    marketCap,
    universeMarketCap,
    sizeSegment,
    selectedUniverse,
    metricsStatus,
    universeId,
  ]);

  return {
    universeId,
    geographicScope,
    sizeSegment,
    cyclicality,
    selectedUniverse,
    availableScopes,
    stats,
    isStatsLoading,
    // Availability of the CH-measured numbers (countries/constituents/mcap)…
    metricsStatus,
    // …and of the catalogue-owned facts (coverage), which are static index
    // metadata and must keep rendering when ClickHouse is down.
    catalogueStatus,
    disabledScopes,
    disabledSizeSegments,
    disabledCyclicality,
    handleUniverseSelect,
    handleGeographicToggle,
    handleSizeToggle,
    handleCyclicalityToggle,
  };
}
